import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Col, Row, Card, CardBody, CardTitle, Container, Table, Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { JobWidgetCharts } from "../DashboardJob/JobCharts";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ScatterChart, Scatter, ZAxis, BarChart, Bar } from 'recharts';


const Vendors = () => {
  const [vulnerabilitiesData, setVulnerabilitiesData] = useState([]);
  const [randomData, setRandomData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cardData, setCardData] = useState([]);
  const [artifactsData, setArtifactsData] = useState([]);
  const [enabledVendors, setEnabledVendors] = useState({});
  const [visibleVendors, setVisibleVendors] = useState({});
  const [scatterData, setScatterData] = useState([]);






  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj?.username;



  ;

  const getFilteredScatterData = (severity) => {
    return scatterData
      .filter((item) => {
        // Check if severity matches
        const severityMatches = item.severity === severity;

        // Check if vendor is enabled using the isEnabled property
        const isVendorEnabled = item.isEnabled;

        // Only return true if both conditions are met
        return severityMatches && isVendorEnabled;
      })
      .map((item) => ({
        // Calculate adjusted scores for the scatter plot
        ...item,
        adjustedImpact: 0.6 * item.impact + 0.4 * item.base,
        adjustedExploitability: 0.6 * item.exploitability + 0.4 * item.base
      }));
  };




  const handleVendorDoubleClick = async (vendorName) => {
    try {
      // Find the current vendor's data
      const vendor = scatterData.find(v => v.name === vendorName);
      if (!vendor) return;

      // Prepare the new enabled state (opposite of current)
      const newEnabledState = !vendor.isEnabled;

      // Send request to backend
      const response = await fetch(`https://api.bluhawk.live/compliance/vendor/${userId}/enable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vendorName: vendorName,
          isEnabled: newEnabledState
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update vendor status');
      }

      // Update local state only after successful backend update
      setEnabledVendors(prev => ({
        ...prev,
        [vendorName]: newEnabledState
      }));

      // Update scatterData to reflect the change
      setScatterData(prev => prev.map(v =>
        v.name === vendorName
          ? { ...v, isEnabled: newEnabledState }
          : v
      ));

    } catch (error) {
      console.error("Error updating vendor status:", error);
      // Optionally add user notification here
    }
  };

  // Initialize visibility state on component mount
  useEffect(() => {
    const initialEnabled = {};
    scatterData.forEach(vendor => {
      initialEnabled[vendor.name] = vendor.isEnabled; // Use isEnabled from the data
    });
    setEnabledVendors(initialEnabled);
  }, [scatterData]);

  const renderVendorCard = (vendor, index, color) => (
    <Col sm="4" key={index}>
      <Card
        className="shadow-sm mb-4"
        style={{
          outline: `2px solid var(--bs-${color})`,
          borderRadius: '10px',
          cursor: 'pointer',
          opacity: vendor.isEnabled ? 1 : 0.5,
          transition: 'opacity 0.3s ease'
        }}
        onDoubleClick={() => handleVendorDoubleClick(vendor.name)}
      >
        <CardBody>
          {/* Header with vendor name and badges */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-2">{vendor.name}</h5>
              <Badge color={color}>
                {vendor.severity}
              </Badge>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Badge color={vendor.severity.toLowerCase() === 'low' ? "success" : "secondary"}>
                <i className="bx bx-shield-quarter me-1"></i>
                {vendor.frameworks.length} Frameworks
              </Badge>
              <i className={`bx ${enabledVendors[vendor.name] ? 'bx-check-circle text-success' : 'bx-x-circle text-danger'} fs-5`}></i>
            </div>
          </div>

          {/* Security Information Section */}
          <div className="mt-3 pt-3 border-top">
            <h6 className="text-muted mb-2">Security & Data Exposure</h6>
            <Row>
              {/* First Column of Security Info */}
              <Col md="6">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>Security Controls:</span>
                  <span>
                    <i className={`bx ${vendor.securityControls === 'High' ? 'bx-check-circle text-success' : 'bx-x-circle text-danger'}`} />
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>Data Exposure Risk:</span>
                  <span>
                    <i className={`bx ${vendor.dataExposureRisk === 'High' ? 'bx-x-circle text-danger' :
                      vendor.dataExposureRisk === 'Moderate' ? 'bx-minus-circle text-warning' : 'bx-check-circle text-success'}`} />
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>Encryption at Rest:</span>
                  <span>
                    <i className={`bx ${vendor.encryptionAtRest ? 'bx-check-circle text-success' : 'bx-x-circle text-danger'}`} />
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>Encryption in Transit:</span>
                  <span>
                    <i className={`bx ${vendor.encryptionInTransit ? 'bx-check-circle text-success' : 'bx-x-circle text-danger'}`} />
                  </span>
                </div>
              </Col>

              {/* Second Column of Security Info */}
              <Col md="6">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>Event Logging:</span>
                  <span>
                    <i className={`bx ${vendor.loggingEnabled ? 'bx-check-circle text-success' : 'bx-x-circle text-danger'}`} />
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>Data Exposure Type:</span>
                  <span>
                    <i className={`bx ${vendor.dataTypeExposure === 'High' ? 'bx-error text-danger' :
                      vendor.dataTypeExposure === 'Moderate' ? 'bx-error text-warning' : 'bx-check-circle text-success'}`} />
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>Redundancy:</span>
                  <span>
                    <i className={`bx ${vendor.redundancy ? 'bx-check-circle text-success' : 'bx-x-circle text-danger'}`} />
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span>Multi-Factor Authentication:</span>
                  <span>
                    <i className={`bx ${vendor.mfaEnabled ? 'bx-check-circle text-success' : 'bx-x-circle text-danger'}`} />
                  </span>
                </div>
              </Col>
            </Row>

            {/* Frameworks Section */}
            <div className="mt-2">
              <div className="d-flex flex-wrap gap-1">
                {vendor.frameworks.map((framework, idx) => (
                  <Badge
                    key={idx}
                    color="light"
                    className="text-dark"
                  >
                    {framework}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  const FloatingAddButton = () => (
    <Link
      to="/add-vendor"
      className="position-fixed"
      style={{
        bottom: '2rem',
        right: '2rem',
        zIndex: 1000,
      }}
    >
      <Button
        color="primary"
        className="rounded-circle shadow-lg d-flex align-items-center justify-content-center"
        style={{
          width: '60px',
          height: '60px',
          fontSize: '24px'
        }}
      >
        <i className="bx bx-plus"></i>
      </Button>
    </Link>
  );



  const COLORS = {
    Critical: '#DC3545', // danger red
    High: '#FD7E14',    // orange
    Medium: '#FFC107',  // warning yellow
    Low: '#17A2B8',     // info blue
    fixed: '#28A745',   // success green
    total: '#4F46E5'    // purple
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);

      const scatterdataresponse = await fetch(`https://api.bluhawk.live/compliance/vendor/${userId}`);
      const ResponseData = await scatterdataresponse.json();
      if (ResponseData && Object.keys(ResponseData).length > 0) {
        setScatterData(ResponseData);
      }


    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const initialVisibility = {};
    scatterData.forEach(vendor => {
      initialVisibility[vendor.name] = true;
    });
    setVisibleVendors(initialVisibility);
  }, []);



  document.title = "Main Dashboard";

  const isDataMissing = !isLoading && (
    scatterData.length === 0
  );

  const EmptyState = () => (
    <Card>
      <div className="text-center p-5">
        <div className="mb-4">
          <i className="mdi mdi-file-search text-primary" style={{ fontSize: "4rem" }} />
        </div>
        <h3 className="mb-3 font-weight-bold">No Insights Available Yet</h3>
        <p className="text-muted mb-4">
          It seems like no data is available at the moment. This could be due to recent activity or a delay in data updates.
          You can start by initiating scans or reviewing your recent actions.
        </p>
        <div className="d-flex justify-content-center gap-3">
          <Link to="/addentities" className="btn btn-primary px-4 py-2">
            Analyze
          </Link>
        </div>
      </div>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const metrics = {
    totalVendors: scatterData.length,
    criticalVendors: scatterData.filter(v => v.severity === 'Critical').length,
    compliantVendors: scatterData.filter(v => v.frameworks.length >= 4).length,
    avgRiskScore: (scatterData.reduce((acc, v) => acc + v.base, 0) / scatterData.length).toFixed(1),
    secureVendors: scatterData.filter(v =>
      v.encryptionAtRest &&
      v.encryptionInTransit &&
      v.mfaEnabled &&
      v.loggingEnabled
    ).length,
    dataProviders: scatterData.filter(v => v.category === 'Database Provider').length,
    mspCount: scatterData.filter(v => v.category === 'MSP').length,
    activeVendors: scatterData.filter(v => v.isEnabled == true).length
  };
  const getAddedThisMonth = (vendors) => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfMonthEpoch = startOfMonth.getTime();
    
    return vendors.filter(vendor => vendor.createdAt >= startOfMonthEpoch).length;
  };
  const getHealthColor = (card) => {
    // Determine card health color based on title or metrics
    if (card.title === 'Critical Risk Vendors' && metrics.criticalVendors > 0) {
      return {
        bg: 'bg-danger-subtle',
        text: 'text-danger',
        indicator: 'bg-danger'
      };
    } else if (card.title === 'Average Risk Score' && metrics.avgRiskScore > 7) {
      return {
        bg: 'custom-orange-bg',
        text: 'text-orange',
        indicator: 'bg-orange'
      };
    } else if (card.title === 'Total Active Vendors'|| card.title === 'MSP Count' || card.title === 'Data Providers' || card.title === 'Active Integrations') {
      return {
        bg: 'bg-info-subtle',
        text: 'text-info',
        indicator: 'bg-info'
      };
      
    } else if (card.title === 'Secure Vendors' && metrics.secureVendors === metrics.totalVendors) {
      return {
        bg: 'bg-success-subtle',
        text: 'text-success',
        indicator: 'bg-success'
      };
    } else if (card.value < 50 || (card.title === 'Average Risk Score' && metrics.avgRiskScore > 5)) {
      return {
        bg: 'bg-warning-subtle',
        text: 'text-warning',
        indicator: 'bg-warning'
      };
    } else if (card.value < 75) {
      return {
        bg: 'bg-info-subtle',
        text: 'text-info',
        indicator: 'bg-info'
      };
    } else {
      return {
        bg: 'bg-success-subtle',
        text: 'text-success',
        indicator: 'bg-success'
      };
    }
  };

  const summarycards = [
    {
      title: 'Total Active Vendors',
      value: metrics.totalVendors,
      delta: `+${getAddedThisMonth(scatterData)} this month`,
      icon: 'bx bx-building-house',
      trend: 'up'
    },
    {
      title: 'Critical Risk Vendors',
      value: metrics.criticalVendors,
      delta: 'Needs immediate attention',
      icon: 'bx bx-shield-x',
      trend: 'down'
    },
    {
      title: 'Compliant Vendors',
      value: metrics.compliantVendors,
      delta: `${((metrics.compliantVendors / metrics.totalVendors) * 100).toFixed(0)}% of total`,
      icon: 'bx bx-check-shield',
      trend: 'up'
    },
    {
      title: 'Average Risk Score',
      value: metrics.avgRiskScore,
      delta: 'Out of 10',
      icon: 'bx bx-error-circle',
      trend: 'neutral'
    },
    {
      title: 'Secure Vendors',
      value: metrics.secureVendors,
      delta: 'All security controls enabled',
      icon: 'bx bx-lock-alt',
      trend: 'up'
    },
    {
      title: 'Data Providers',
      value: metrics.dataProviders,
      delta: 'Database & Storage',
      icon: 'bx bx-data',
      trend: 'neutral'
    },
    {
      title: 'MSP Count',
      value: metrics.mspCount,
      delta: 'Managed Service Providers',
      icon: 'bx bx-network-chart',
      trend: 'up'
    },
    {
      title: 'Active Integrations',
      value: metrics.activeVendors,
      delta: 'Currently connected',
      icon: 'bx bx-link-alt',
      trend: 'up'
    }
  ];

  const getProgressPercentage = (card, metrics) => {
    switch (card.title) {
      case 'Total Active Vendors':
        // Progress based on active vs total vendors
        return (metrics.activeVendors / metrics.totalVendors) * 100;
        
      case 'Critical Risk Vendors':
        // Inverse percentage - lower is better (0 critical vendors = 100% progress)
        return Math.max(0, 100 - ((metrics.criticalVendors / metrics.totalVendors) * 100));
        
      case 'Compliant Vendors':
        // Direct percentage of compliant vendors
        return (metrics.compliantVendors / metrics.totalVendors) * 100;
        
      case 'Average Risk Score':
        // Inverse of risk score (lower score = higher progress)
        // 10 is max score, 0 is best score
        return Math.max(0, (metrics.avgRiskScore * 10));
        
      case 'Secure Vendors':
        // Percentage of vendors that are secure
        return (metrics.secureVendors / metrics.totalVendors) * 100;
        
      case 'Data Providers':
        // Percentage of total that are data providers
        return (metrics.dataProviders / metrics.totalVendors) * 100;
        
      case 'MSP Count':
        // Percentage of total that are MSPs
        return (metrics.mspCount / metrics.totalVendors) * 100;
        
      case 'Active Integrations':
        // Percentage of active vs total vendors
        return (metrics.activeVendors / metrics.totalVendors) * 100;
        
      default:
        return 50;
    }
  };



  return (
    <React.Fragment>
      <div className="page-content" style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <Breadcrumbs breadcrumbItem="Home" />
        <Container fluid className="d-flex flex-column">
          {isDataMissing ? (
            <EmptyState />
          ) : (
            <>
              <Row>
                <Card className="shadow-sm">
                  <div style={{ height: "600px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart
                        margin={{ top: 20, right: 30, bottom: 40, left: 40 }}
                      >
                        {/* Background Quadrants */}
                        <defs>
                          <clipPath id="quadrantClip">
                            <rect x="0" y="0" width="100%" height="100%" />
                          </clipPath>
                        </defs>

                        {/* Add this section for quadrant background colors */}
                        <g clipPath="url(#quadrantClip)">
                          <rect x="70%" y="0" width="30%" height="30%" fill="#ffebee" fillOpacity="0.2" /> {/* Critical Risk */}
                          <rect x="70%" y="30%" width="30%" height="70%" fill="#fff3e0" fillOpacity="0.2" /> {/* High Impact */}
                          <rect x="0" y="0" width="70%" height="30%" fill="#e3f2fd" fillOpacity="0.2" /> {/* Easily Exploitable */}
                          <rect x="0" y="30%" width="70%" height="70%" fill="#f1f8e9" fillOpacity="0.2" /> {/* Lower Risk */}
                        </g>

                        <CartesianGrid strokeDasharray="3 3" strokeWidth={0.5} />

                        {/* Quadrant Lines */}
                        <line
                          x1="33%"
                          y1="0"
                          x2="33%"
                          y2="100%"
                          stroke="#666"
                          strokeDasharray="3 3"
                          strokeWidth={1}
                        />
                        <line
                          x1="66%"
                          y1="0"
                          x2="66%"
                          y2="100%"
                          stroke="#666"
                          strokeDasharray="3 3"
                          strokeWidth={1}
                        />


                        <XAxis
                          type="number"
                          dataKey="adjustedImpact"
                          domain={[0, 10]}
                          label={{
                            value: "Vendor Impact Score",
                            position: "bottom",
                          }}
                          ticks={[0, 2, 4, 6, 8, 10]}
                        />
                        <YAxis
                          type="number"
                          dataKey="adjustedExploitability"
                          domain={[0, 10]}
                          label={{
                            value: "Vendor Risk Score",
                            angle: -90,
                            position: "left",
                          }}
                          ticks={[0, 2, 4, 6, 8, 10]}
                        />

                        <Tooltip
                          cursor={{ strokeDasharray: "3 3" }}
                          content={({ payload }) => {
                            if (payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-white p-3 shadow-lg border rounded">
                                  <p className="font-bold text-lg mb-1">{data.name}</p>
                                  <p className="mb-1">Category: {data.category}</p>
                                  <p className="mb-1">Impact: {data.impact}</p>
                                  <p className="mb-1">Exploitability: {data.exploitability}</p>
                                  <p className="mb-1">Frameworks: {data.frameworks.join(", ")}</p>
                                  <p className="mb-0">Last Assessment: {data.lastAssessment}</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />

                        <Legend verticalAlign="top" height={36} />
                        {["Critical", "High", "Medium", "Low"].map((severity) => (
                          <Scatter
                            key={severity}
                            name={severity}
                            data={getFilteredScatterData(severity)}
                            fill={COLORS[severity]}
                            shape={(props) => {
                              const { cx, cy, payload } = props;
                              // Much larger size difference based on frameworks
                              const baseSize = 8;
                              const frameworksBonus = payload.frameworks.length * 3; // Multiplied by 3 for more visible difference
                              const size = baseSize + frameworksBonus;

                              return (
                                <circle
                                  cx={cx}
                                  cy={cy}
                                  r={size}
                                  fill='none'
                                  stroke={COLORS[payload.severity]}
                                  strokeWidth={2}
                                />
                              );
                            }}
                          />
                        ))}
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>

                </Card>
              </Row>
              <Row className="mb-6">
          {summarycards.map((card, index) => {
            const colors = getHealthColor(card);
            const progressPercent = getProgressPercentage(card, metrics);

            return (
              <Col key={index} lg={3} md={4} sm={6} xs={12} className="mb-4">
<Card 
                  className={`${colors.bg} border-0 shadow-sm hover:shadow-lg transition-all duration-300`}
                  style={colors.bg === 'custom-orange-bg' ? { backgroundColor: 'rgba(253, 126, 20, 0.25)' } : {}}
                >                  <CardBody>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className={`rounded-circle p-2 ${colors.bg}`}>
                        <i className={`${card.icon} ${colors.text} fs-4`}></i>
                      </div>
                      <div className={`rounded-pill px-2 py-1 ${colors.bg} ${colors.text} small`}>
                        {card.trend === 'up' && <i className="bx bx-up-arrow-alt me-1"></i>}
                        {card.trend === 'down' && <i className="bx bx-down-arrow-alt me-1"></i>}
                        {card.trend === 'neutral' && <i className="bx bx-minus me-1"></i>}
                        {card.delta}
                      </div>
                    </div>
                    
                    <h4 className={`${colors.text} fw-bold mb-1`}>
                      {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                    </h4>
                    
                    <p className="text-muted mb-0 fs-6">
                      {card.title}
                    </p>
                    
                    <div className={`progress mt-3 bg-white bg-opacity-50`} style={{height: "4px"}}>
                    <div 
                      className={`progress-bar ${colors.indicator}`} 
                      role="progressbar" 
                      style={{
                        width: `${Math.min(progressPercent, 100)}%`
                      }}
                      aria-valuenow={Math.min(progressPercent, 100)}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>


              <Row>
                {/* Package Details */}
                {scatterData.reduce((acc, curr) => {
                  if (!acc.includes(curr.category)) {
                    acc.push(curr.category);
                  }
                  return acc;
                }, []).map((category) => (
                  <div key={category} className="mt-4">
                    <h4 className="mb-3">
                      {category}
                    </h4>
                    <Row>
                      {scatterData
                        .filter(vendor => vendor.category === category)
                        .map((vendor, index) => {
                          // Map severity to Bootstrap color scheme
                          const getSeverityColor = (severity) => {
                            switch (severity.toLowerCase()) {
                              case 'critical': return 'danger';
                              case 'high': return 'orange';
                              case 'medium': return 'warning';
                              case 'low': return 'info';
                              default: return 'secondary';
                            }
                          };

                          const color = getSeverityColor(vendor.severity);

                          return renderVendorCard(vendor, index, color);

                        })}
                    </Row>
                  </div>
                ))}



              </Row>
              <FloatingAddButton />

            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Vendors;